import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Wrapper para carruseles horizontales que muestra dos pills semi-
// transparentes (back / forward) al hacer hover sobre el área del
// carrusel — solo en desktop (`md+`). Los pills disparan un scroll
// suave del ~80% del ancho visible y se ocultan automáticamente cuando
// no hay más para scrollear en esa dirección.
//
// Reemplaza directamente al `<div className="flex overflow-x-auto ...">`
// que envuelve los items: se le pasan las mismas `className` / `style`
// que tendría ese div. La animación CSS (`animate-mode-swap`) sigue
// funcionando porque el div interno se remonta junto con el wrapper
// cuando el `key` del padre cambia.

interface HoverScrollCarouselProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
  /** Offset en píxeles entre el pill y el borde del wrapper. Más chico
   *  = más pegado al borde. Default: 6. */
  arrowInset?: number;
}

export const HoverScrollCarousel: React.FC<HoverScrollCarouselProps> = ({
  className,
  style,
  children,
  arrowInset = 6,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // Recalculamos los affordances (¿hay más para scrollear?) en cada
  // scroll, resize del container y cambio de children. 4px de tolerancia
  // para evitar parpadeo del pill cuando el browser deja un sub-pixel
  // residual.
  const updateAffordance = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateAffordance();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateAffordance);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateAffordance, children]);

  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  // `transition-opacity` (no `transition-all`) + clase neutra de animación
  // para que los pills NO hereden el `animate-mode-swap` (translate+fade)
  // del scroll container cuando éste lo aplica. Sin esto, los pills
  // "se deslizan hacia abajo" al cliquearlos porque la animación CSS se
  // re-aplica al remount del wrapper.
  //
  // El `group-hover/hsc:opacity-100` se aplica condicionalmente desde
  // cada botón (solo cuando hay scroll en esa dirección), porque su
  // specificity gana al `disabled:opacity-0` y el pill se "asoma" en
  // hover aunque esté disabled.
  const arrowBase =
    'hidden md:inline-flex absolute top-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-black/45 hover:bg-black/65 text-white backdrop-blur-md shadow-md opacity-0 transition-opacity duration-200 focus:outline-none';
  // `transform` aparte del Tailwind para que no entre en conflicto con
  // animaciones de translate que pueda heredar el wrapper. Lo dejamos
  // hard-coded por inline style.
  const arrowStyle: React.CSSProperties = { transform: 'translateY(-50%)' };

  return (
    // `w-full min-w-0` evita que el wrapper se shrink-to-fit en contextos
    // flex (ej. el sheet de ModelPicker, que pone el carousel adentro de
    // un `flex items-center`). Sin esto el wrapper relative quedaba con
    // ancho 0 y el inner scroll div no podía scrollear horizontalmente.
    <div className="group/hsc relative w-full min-w-0">
      <div
        ref={scrollRef}
        className={className}
        style={style}
        onScroll={updateAffordance}
      >
        {children}
      </div>
      <button
        type="button"
        aria-label="Anterior"
        // `onMouseDown.preventDefault` evita que el botón tome focus al
        // click — sin focus el browser no intenta hacer scroll-into-view
        // y el pill no "salta".
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => scrollByDir(-1)}
        disabled={!canPrev}
        aria-hidden={!canPrev}
        style={{ ...arrowStyle, left: arrowInset }}
        className={`${arrowBase} ${canPrev ? 'group-hover/hsc:opacity-100' : 'pointer-events-none'}`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        aria-label="Siguiente"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => scrollByDir(1)}
        disabled={!canNext}
        aria-hidden={!canNext}
        style={{ ...arrowStyle, right: arrowInset }}
        className={`${arrowBase} ${canNext ? 'group-hover/hsc:opacity-100' : 'pointer-events-none'}`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};
